import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { downloadFile } from "lib/utils";
import { BasicDocumentDetails } from "./useDownloadBidDocument";
import { useGetBidDocumentUrl } from "./useGetBidDocumentUrl";

type Params = {
  documents: BasicDocumentDetails[];
  opportunityId: string;
};
export function useDownloadAllBidDocuments(
  options?: UseMutationOptions<void, unknown, Params, unknown>,
) {
  const { mutateAsync: getBidDocumentUrlMutation } = useGetBidDocumentUrl();

  const mutation = useMutation<void, AxiosError, Params>(async ({ documents, opportunityId }) => {
    // Dynamically import `fflate`
    const { zipSync } = await import("fflate");

    // Fetch all document URLs
    const fileFetchPromises = documents.map(async ({ documentId, fileName }) => {
      const { getBidDocumentUrl } = await getBidDocumentUrlMutation({ id: documentId });
      const documentUrl = getBidDocumentUrl.documentUrl;

      if (!documentUrl) throw new Error(`Failed to retrieve document URL for ${fileName}`);

      // Fetch file as Blob
      const response = await fetch(documentUrl);
      if (!response.ok) throw new Error(`Failed to download ${fileName}`);

      const blob = await response.blob();
      return { fileName, blob };
    });

    const files = await Promise.all(fileFetchPromises);

    // Convert blobs to Uint8Array
    const fileMap: Record<string, Uint8Array> = {};
    for (const { fileName, blob } of files) {
      const arrayBuffer = await blob.arrayBuffer();
      fileMap[fileName] = new Uint8Array(arrayBuffer);
    }

    // Create zip archive
    const zipData = zipSync(fileMap);

    // Trigger download
    const zipBlob = new Blob([zipData], { type: "application/zip" });
    // Download the zip file
    downloadFile(zipBlob, `stotles_bid_documents_${opportunityId}`);
  }, options);

  return { ...mutation };
}
